import { ShieldAlert } from 'lucide-react'
import { useState } from 'react'

import { PageHeader } from '@/components/page-header'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { LedgerAuditLookupSection } from '@/features/ledger/audit-lookup'
import { BusinessDayScreen } from '@/features/ledger/business-day-panels'
import { ChartOfAccountsSection } from '@/features/ledger/chart-of-accounts'
import { DailyBalancesSection } from '@/features/ledger/daily-balances'
import { BalanceDriftSection } from '@/features/ledger/drift-alerts'
import { JournalEnquirySection } from '@/features/ledger/journal-list'
import { LedgerApprovalRouting, LedgerCapabilityGaps } from '@/features/ledger/ledger-gaps'
import { ManualJournalSection } from '@/features/ledger/manual-journal'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from '@/features/ledger/permissions'
import { PostingPeriodsSection } from '@/features/ledger/posting-periods'
import { ProvisionRunSection } from '@/features/ledger/provision-run'
import { TrialBalanceSection } from '@/features/ledger/trial-balance'

const PANEL_ID = 'ledger-panel'

type LedgerTab =
  | 'business-days'
  | 'journals'
  | 'manual-journal'
  | 'accounts'
  | 'trial-balance'
  | 'balances'
  | 'periods'
  | 'drift'
  | 'provisioning'
  | 'audit'

/**
 * The ledger.
 *
 * Posted entries are immutable: a correction is a reversal plus a fresh posting, never an edit, and
 * nothing posts into a closed day or a closed period. That rule is what every screen in this module
 * is arranged around, and it is stated at the top because it is the first thing that surprises
 * somebody arriving from any other part of the application.
 *
 * The sections are tabs rather than sub-routes, and that is a constraint honestly taken rather than
 * a preference: each one is also exported as a standalone screen (see `./index.ts`), so registering
 * `/ledger/trial-balance` and its siblings in the router tree adds addressable routes without
 * changing anything here. Until they are registered, a typed `<Link>` to a path the route tree does
 * not carry would not compile, so the strip switches in place.
 *
 * Width is decided per section rather than for the module, and the split is the one
 * `docs/mobile-policy.md` argues. Reading whether a branch is trading, reading a journal, reading a
 * trial balance: all fine on a phone, and all things somebody genuinely asks between places. Keying
 * a manual journal and closing a day: not, and those two refuse rather than degrade.
 */
export function LedgerPage() {
  const permissions = useLedgerPermissions()
  const [tab, setTab] = useState<LedgerTab>('business-days')

  if (!permissions.canEnterModule) {
    return (
      <>
        <PageHeader
          title="Ledger"
          description="Chart of accounts, journals, trial balance, business day and period control."
        />
        <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
          <ShieldAlert className="size-6 text-warning-text" aria-hidden />
          <p className="text-sm font-medium text-heading">The ledger is restricted</p>
          <p className="max-w-lg text-xs text-muted">
            Nothing in this module is open to your role. Its screens are gated on the ledger
            permissions — <span className="font-mono">{LEDGER_PERMISSIONS.journalView}</span>,{' '}
            <span className="font-mono">{LEDGER_PERMISSIONS.dayOpen}</span>,{' '}
            <span className="font-mono">{LEDGER_PERMISSIONS.trialBalanceView}</span> and their
            siblings — and the server checks each of them again on every request.
          </p>
        </div>
      </>
    )
  }

  /*
   * The strip is filtered by permission rather than rendered whole with dead tabs. A tab that opens
   * onto a refusal panel teaches nothing the navigation could not have said by omission — and this
   * module has ten sections, which is already more than a strip should carry for somebody whose role
   * touches two of them. Every section still gates itself: the routes are directly addressable.
   */
  const tabs: TabDefinition<LedgerTab>[] = [
    { id: 'business-days' as const, label: 'Business days', show: true },
    { id: 'journals' as const, label: 'Journals', show: permissions.canViewJournals },
    { id: 'manual-journal' as const, label: 'Manual journal', show: permissions.canPostManualJournal },
    { id: 'accounts' as const, label: 'Chart of accounts', show: permissions.canViewAccounts },
    { id: 'trial-balance' as const, label: 'Trial balance', show: permissions.canViewTrialBalance },
    { id: 'balances' as const, label: 'Daily balances', show: permissions.canViewTrialBalance },
    {
      id: 'periods' as const,
      label: 'Periods',
      show: permissions.canClosePeriod || permissions.canReopenPeriod || permissions.canViewJournals,
    },
    { id: 'drift' as const, label: 'Balance drift', show: permissions.canViewDrift },
    { id: 'provisioning' as const, label: 'Provisioning', show: permissions.canRunProvision },
    { id: 'audit' as const, label: 'Audit trail', show: permissions.canReadAudit },
  ]
    .filter((candidate) => candidate.show)
    .map(({ id, label }) => ({ id, label }))

  return (
    <>
      <PageHeader
        title="Ledger"
        description="Posted entries are immutable: a correction is a reversal plus a fresh posting, never an edit, and nothing posts into a closed day or a closed period."
      />

      <div className="space-y-4">
        <TabStrip<LedgerTab>
          tabs={tabs}
          active={tab}
          onChange={setTab}
          panelId={PANEL_ID}
          label="Ledger sections"
        />

        <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
          {tab === 'business-days' ? <BusinessDayScreen /> : null}
          {tab === 'journals' ? <JournalEnquirySection /> : null}
          {tab === 'manual-journal' ? <ManualJournalSection /> : null}
          {tab === 'accounts' ? <ChartOfAccountsSection /> : null}
          {tab === 'trial-balance' ? <TrialBalanceSection /> : null}
          {tab === 'balances' ? <DailyBalancesSection /> : null}
          {tab === 'periods' ? <PostingPeriodsSection /> : null}
          {tab === 'drift' ? <BalanceDriftSection /> : null}
          {tab === 'provisioning' ? <ProvisionRunSection /> : null}
          {tab === 'audit' ? <LedgerAuditLookupSection /> : null}
        </div>
      </div>

      <LedgerApprovalRouting />
      <LedgerCapabilityGaps />
    </>
  )
}
